import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { isPast } from "date-fns";
import { 
  ClipboardList, Plus, Calendar, CheckCircle2, Clock, Filter,
  Search, Sparkles, AlertTriangle
} from "lucide-react";
import { db } from "@/lib/supabaseUntyped";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { DemarcheCard } from "@/components/demarches/DemarcheCard";
import { CreateDemarcheDialog } from "@/components/demarches/CreateDemarcheDialog";
import { OverdueStepsAlert } from "@/components/demarches/OverdueStepsAlert";

type FilterStatus = 'all' | 'overdue' | 'pending' | 'in_progress' | 'completed';

interface Demarche {
  id: string;
  title: string;
  description: string | null;
  template_key: string | null;
  status: string;
  due_date: string | null;
  completed_at: string | null;
  auto_generated: boolean;
  official_url: string | null;
  for_member_id: string | null;
  created_at: string;
  // Computed
  documents_count?: number;
  documents_completed?: number;
  steps_count?: number;
  steps_completed?: number;
}

export default function Demarches() {
  const navigate = useNavigate();
  const [demarches, setDemarches] = useState<Demarche[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const fetchDemarches = async () => {
    try {
      const { data: user } = await db.auth.getUser();
      if (!user.user) return;

      // Fetch demarches with document and step counts
      const { data: demarchesData, error } = await db
        .from('demarches')
        .select(`
          *,
          demarche_documents(id, is_completed),
          demarche_steps(id, is_completed)
        `)
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const processed = (demarchesData || []).map((d: any) => ({
        ...d,
        documents_count: d.demarche_documents?.length || 0,
        documents_completed: d.demarche_documents?.filter((doc: any) => doc.is_completed).length || 0,
        steps_count: d.demarche_steps?.length || 0,
        steps_completed: d.demarche_steps?.filter((s: any) => s.is_completed).length || 0,
      }));

      setDemarches(processed);
    } catch (error) {
      console.error('Error fetching demarches:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemarches();
  }, []);

  // Helper to check if overdue
  const isOverdue = (d: Demarche) => 
    d.due_date && isPast(new Date(d.due_date)) && d.status !== 'completed';

  // Filter and search
  const filteredDemarches = useMemo(() => {
    let result = demarches;

    // Filter by status (overdue is a computed state)
    if (filter === 'overdue') {
      result = result.filter(d => isOverdue(d));
    } else if (filter !== 'all') {
      result = result.filter(d => d.status === filter);
    }

    // Search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(d => 
        d.title.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q)
      );
    }

    return result;
  }, [demarches, filter, search]);

  // Stats
  const stats = useMemo(() => ({
    total: demarches.length,
    overdue: demarches.filter(d => isOverdue(d)).length,
    pending: demarches.filter(d => d.status === 'pending').length,
    in_progress: demarches.filter(d => d.status === 'in_progress').length,
    completed: demarches.filter(d => d.status === 'completed').length,
    auto_generated: demarches.filter(d => d.auto_generated).length,
  }), [demarches]);

  const handleDemarcheClick = (id: string) => {
    navigate(`/demarches/${id}`);
  };

  const handleDemarcheCreated = () => {
    setCreateDialogOpen(false);
    fetchDemarches();
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10">
              <ClipboardList className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Mes démarches</h1>
              <p className="text-sm text-muted-foreground">
                {stats.overdue > 0 && (
                  <span className="text-destructive font-medium">{stats.overdue} en retard · </span>
                )}
                {stats.pending + stats.in_progress} en cours · {stats.completed} terminées
              </p>
            </div>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nouvelle démarche</span>
          </Button>
        </div>

        {/* Overdue steps alert */}
        <OverdueStepsAlert />

        {/* Quick stats */}
        {!loading && demarches.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {/* Overdue - only show if there are overdue items */}
            {stats.overdue > 0 && (
              <button 
                onClick={() => setFilter('overdue')}
                className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 hover:bg-destructive/15 transition-colors text-left"
              >
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-lg font-bold">{stats.overdue}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">En retard</p>
              </button>
            )}
            <button 
              onClick={() => setFilter('pending')}
              className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/15 transition-colors text-left"
            >
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <Clock className="w-4 h-4" />
                <span className="text-lg font-bold">{stats.pending}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">En attente</p>
            </button>
            <button 
              onClick={() => setFilter('in_progress')}
              className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/15 transition-colors text-left"
            >
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <Calendar className="w-4 h-4" />
                <span className="text-lg font-bold">{stats.in_progress}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">En cours</p>
            </button>
            <button 
              onClick={() => setFilter('completed')}
              className="p-3 rounded-xl bg-green-500/10 border border-green-500/20 hover:bg-green-500/15 transition-colors text-left"
            >
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-lg font-bold">{stats.completed}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Terminées</p>
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une démarche..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterStatus)}>
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="all" className="text-xs">Toutes</TabsTrigger>
              <TabsTrigger value="overdue" className="text-xs gap-1">
                En retard
                {stats.overdue > 0 && (
                  <Badge variant="destructive" className="ml-1 px-1.5 py-0 text-[10px] h-4">
                    {stats.overdue}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="pending" className="text-xs">En attente</TabsTrigger>
              <TabsTrigger value="in_progress" className="text-xs">En cours</TabsTrigger>
              <TabsTrigger value="completed" className="text-xs">Terminées</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        ) : filteredDemarches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            {demarches.length === 0 ? (
              <>
                <div className="p-4 rounded-full bg-muted mb-4">
                  <Sparkles className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Aucune démarche en cours</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                  Créez votre première démarche pour suivre vos documents et étapes administratives.
                </p>
                <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Créer une démarche
                </Button>
              </>
            ) : (
              <>
                <div className="p-4 rounded-full bg-muted mb-4">
                  <Filter className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Aucun résultat</h3>
                <p className="text-sm text-muted-foreground">
                  Modifiez vos filtres pour voir plus de démarches.
                </p>
              </>
            )}
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            <div className="space-y-3">
              {filteredDemarches.map((demarche, index) => (
                <motion.div
                  key={demarche.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <DemarcheCard
                    demarche={demarche}
                    onClick={() => handleDemarcheClick(demarche.id)}
                  />
                </motion.div>
              ))}
            </div>
          </AnimatePresence>
        )}
      </div>

      {/* Create dialog */}
      <CreateDemarcheDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={handleDemarcheCreated}
      />
    </div>
  );
}
